import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Clock, Info, Send } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSettings } from "@/context/SettingsContext";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { Btn, Section } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";
import { submitContact } from "@/lib/api";

const inputCls =
  "w-full border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors duration-200 focus:border-grn";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-navy/50";

export default function Contact() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const p = t("pages.contact");
  const f = p.form;
  const c = settings?.contact || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organisation: "",
    district: "",
    category: "membership",
    message: "",
  });
  const [sending, setSending] = useState(false);
  useSEO({ title: p.title, description: p.lead, path: "/contact" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const errText = (d) => {
    if (!d) return f.error;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(", ") || f.error;
    return f.error;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 10) {
      toast.error("Please enter your name, a valid email and a message of at least 10 characters.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      const res = await submitContact(payload);
      toast.success(res.message || f.success);
      setForm({ name: "", email: "", phone: "", organisation: "", district: "", category: "membership", message: "" });
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: MapPin, label: p.labels.registeredOffice, value: c.registered_office },
    { icon: MapPin, label: p.labels.supportOffice, value: c.support_office },
    { icon: Phone, label: p.labels.phone, value: c.phone },
    { icon: Mail, label: p.labels.email, value: c.email },
    { icon: MessageCircle, label: p.labels.whatsapp, value: c.whatsapp },
    { icon: Clock, label: p.labels.hours, value: c.office_hours },
  ].filter((d) => d.value);

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="contact-page">
        <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <Reveal>
            <form onSubmit={onSubmit} className="border border-navy/10 bg-white p-8 sm:p-10" data-testid="contact-form">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cf-name">
                    {f.name} *
                  </label>
                  <input id="cf-name" required minLength={2} value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} data-testid="contact-name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-email">
                    {f.email} *
                  </label>
                  <input id="cf-email" type="email" required value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} data-testid="contact-email" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-phone">
                    {f.phone}
                  </label>
                  <input id="cf-phone" value={form.phone} onChange={set("phone")} className={`mt-2 ${inputCls}`} data-testid="contact-phone" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-org">
                    {f.organisation}
                  </label>
                  <input id="cf-org" value={form.organisation} onChange={set("organisation")} className={`mt-2 ${inputCls}`} data-testid="contact-organisation" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-district">
                    {f.district}
                  </label>
                  <input id="cf-district" value={form.district} onChange={set("district")} className={`mt-2 ${inputCls}`} data-testid="contact-district" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cf-category">
                    {f.category} *
                  </label>
                  <select id="cf-category" value={form.category} onChange={set("category")} className={`mt-2 ${inputCls}`} data-testid="contact-category">
                    {f.categories.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cf-message">
                    {f.message} *
                  </label>
                  <textarea
                    id="cf-message"
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className={`mt-2 ${inputCls} resize-none`}
                    data-testid="contact-message"
                  />
                </div>
              </div>

              <p className="mt-6 flex items-start gap-3 border-l-2 border-gold bg-slate-50 p-4 text-[12px] leading-relaxed text-slate-600">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                {p.legalNote}
              </p>

              <Btn
                variant="solid"
                type="submit"
                disabled={sending}
                className="mt-7 w-full disabled:opacity-50 sm:w-auto"
                data-testid="contact-submit"
              >
                {sending ? f.sending : f.submit} <Send className="h-4 w-4" />
              </Btn>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-navy/10 bg-slate-50/70 p-8 sm:p-10" data-testid="contact-details">
              <h2 className="font-display text-xl font-semibold text-navy">Secretariat</h2>
              <dl className="mt-8 space-y-7">
                {details.map((d) => (
                  <div key={d.label} className="flex gap-4">
                    <d.icon className="mt-0.5 h-4 w-4 shrink-0 text-grn" strokeWidth={1.6} />
                    <div>
                      <dt className={labelCls}>{d.label}</dt>
                      <dd className="mt-1.5 text-sm text-navy/80">{d.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
              {details.length <= 2 && (
                <p className="mt-8 text-[12px] leading-relaxed text-slate-500">{t("common.noContentYet")}</p>
              )}
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
